import { useState, useEffect } from "react";
import { supabase } from "../../../lib/supabase";
import ImageUploader from "../../../components/base/ImageUploader";
import RichTextEditor from "../../../components/base/RichTextEditor";
import type { CompanyExtraLink, CompanyPatent } from "../../../lib/supabase";

// Danh mục chính - đồng bộ với mocks/categories.ts
const MAIN_CATEGORIES = [
  { id: "cat-cn", nameVi: "Vật liệu Carbon & Nano", nameKr: "탄소·나노 원소재" },
  { id: "cat-mob", nameVi: "Giao thông Tương lai", nameKr: "미래 모빌리티" },
  { id: "cat-it", nameVi: "IT · Điện tử & Bán dẫn", nameKr: "IT·전자 및 반도체 소재" },
  { id: "cat-eq", nameVi: "Thiết bị & Máy móc Công nghiệp", nameKr: "공정 장비 및 기계" },
  { id: "cat-en", nameVi: "Năng lượng & Pin Thế hệ Mới", nameKr: "에너지 및 차세대 배터리" },
  { id: "cat-cs", nameVi: "Xây dựng & Vật liệu Công nghiệp", nameKr: "건설·건축 및 산업자재" },
  { id: "cat-ft", nameVi: "Sợi Chức năng & Hàng Tiêu dùng", nameKr: "기능성 섬유 및 생활용품" },
  { id: "cat-ot", nameVi: "Ngành khác (Nghiên cứu - Hỗ trợ)", nameKr: "기타 업종 (연구·지원)" },
] as const;

type Lang = "vi" | "kr";
type Tab = "info" | "images" | "patents" | "links";

export interface CompanyRow {
  id: string;
  name: string;
  name_kr?: string;
  tagline_vi?: string;
  tagline_kr?: string;
  description_vi?: string;
  description_kr?: string;
  logo_initials?: string;
  logo_icon?: string;
  logo_from?: string;
  logo_to?: string;
  logo_url?: string;
  founded?: string;
  location?: string;
  employees?: string;
  main_category_name?: string;
  main_category_name_kr?: string;
  category_id?: string;
  website?: string;
  images?: string[];
  extra_links?: CompanyExtraLink[];
  patents_certifications?: CompanyPatent[];
}

interface Props {
  lang: Lang;
  editData?: CompanyRow | null;
  onClose: () => void;
  onSuccess: () => void;
}

const emptyForm = (): CompanyRow => ({
  id: "",
  name: "",
  name_kr: "",
  tagline_vi: "",
  tagline_kr: "",
  description_vi: "",
  description_kr: "",
  logo_initials: "",
  logo_icon: "ri-building-line",
  logo_from: "#1C4E6B",
  logo_to: "#2B7A9C",
  logo_url: "",
  founded: "",
  location: "",
  employees: "",
  main_category_name: "",
  main_category_name_kr: "",
  website: "",
  images: [],
  extra_links: [],
  patents_certifications: [],
});

const PATENT_TYPES = [
  { value: "patent", labelVi: "Bằng sáng chế", labelKr: "특허" },
  { value: "certification", labelVi: "Chứng nhận", labelKr: "인증" },
  { value: "award", labelVi: "Giải thưởng", labelKr: "수상" },
] as const;

const CompanyFormModal: React.FC<Props> = ({ lang, editData, onClose, onSuccess }) => {
  const t = (vi: string, kr: string) => (lang === "vi" ? vi : kr);
  const [activeTab, setActiveTab] = useState<Tab>("info");
  const [form, setForm] = useState<CompanyRow>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [logoImages, setLogoImages] = useState<string[]>([]);
  const [logoMode, setLogoMode] = useState<"upload" | "gradient">("upload");
  const [companyImages, setCompanyImages] = useState<string[]>([]);
  const [extraLinks, setExtraLinks] = useState<CompanyExtraLink[]>([{ label: "", url: "" }]);
  const [patents, setPatents] = useState<CompanyPatent[]>([]);
  const [patentImages, setPatentImages] = useState<Record<number, string[]>>({});

  useEffect(() => {
    if (editData) {
      setForm({ ...emptyForm(), ...editData });
      setLogoImages(editData.logo_url ? [editData.logo_url] : []);
      setLogoMode(editData.logo_url ? "upload" : "gradient");
      setCompanyImages(editData.images ?? []);
      setExtraLinks(editData.extra_links?.length ? editData.extra_links : [{ label: "", url: "" }]);
      setPatents(editData.patents_certifications ?? []);
      const imgMap: Record<number, string[]> = {};
      (editData.patents_certifications ?? []).forEach((p, i) => {
        if (p.image_url) imgMap[i] = [p.image_url];
      });
      setPatentImages(imgMap);
    }
  }, [editData]);

  const set = (field: keyof CompanyRow, val: string) =>
    setForm((prev) => ({ ...prev, [field]: val }));

  // Extra links
  const setLink = (idx: number, field: keyof CompanyExtraLink, val: string) =>
    setExtraLinks((prev) => prev.map((l, i) => (i === idx ? { ...l, [field]: val } : l)));
  const addLink = () => setExtraLinks((prev) => [...prev, { label: "", url: "" }]);
  const removeLink = (idx: number) => setExtraLinks((prev) => prev.filter((_, i) => i !== idx));

  // Patents
  const addPatent = () => setPatents((prev) => [...prev, { title: "", type: "certification" }]);
  const setPatent = (idx: number, field: keyof CompanyPatent, val: string) =>
    setPatents((prev) => prev.map((p, i) => (i === idx ? { ...p, [field]: val } : p)));
  const removePatent = (idx: number) => {
    setPatents((prev) => prev.filter((_, i) => i !== idx));
    setPatentImages((prev) => {
      const next: Record<number, string[]> = {};
      Object.entries(prev).forEach(([k, v]) => {
        const ki = Number(k);
        if (ki < idx) next[ki] = v;
        else if (ki > idx) next[ki - 1] = v;
      });
      return next;
    });
  };
  const setPatentImg = (idx: number, urls: string[]) => {
    setPatentImages((prev) => ({ ...prev, [idx]: urls }));
    setPatent(idx, "image_url", urls[0] ?? "");
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError(t("Vui lòng nhập tên công ty!", "회사명을 입력해주세요!"));
      return;
    }
    // Auto-generate ID if not editing and no ID set
    if (!form.id.trim()) {
      const autoId = `co-${form.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 20)}-${Date.now()}`;
      setForm((prev) => ({ ...prev, id: autoId }));
      form.id = autoId;
    }
    setSaving(true);
    setError("");
    const cleanLinks = extraLinks.filter((l) => l.url.trim());
    const cleanPatents = patents.map((p, i) => ({
      ...p,
      image_url: (patentImages[i] ?? [])[0] ?? p.image_url ?? "",
    })).filter((p) => p.title.trim());

    const finalLogoUrl = logoImages[0] ?? form.logo_url ?? "";
    const payload = {
      ...form,
      logo_url: finalLogoUrl,
      images: companyImages,
      extra_links: cleanLinks,
      patents_certifications: cleanPatents,
      updated_at: new Date().toISOString(),
    };
    const { error: dbErr } = await supabase.from("companies").upsert(payload, { onConflict: "id" });
    if (dbErr) { setSaving(false); setError(dbErr.message); return; }

    // Auto-sync company_logo into san_pham_han_quoc for all products of this company
    if (finalLogoUrl) {
      await supabase
        .from("san_pham_han_quoc")
        .update({ company_logo: finalLogoUrl })
        .eq("company_id", form.id);
      // Also try matching by company name (trimmed)
      await supabase
        .from("san_pham_han_quoc")
        .update({ company_logo: finalLogoUrl, company_id: form.id })
        .ilike("company_name", form.name.trim());
    }

    setSaving(false);
    onSuccess();
  };

  const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:border-[#004A99] outline-none bg-white";
  const labelCls = "block text-xs font-semibold text-gray-600 mb-1";
  const logoPreviewBg = `linear-gradient(135deg, ${form.logo_from ?? "#1C4E6B"}, ${form.logo_to ?? "#2B7A9C"})`;

  const TABS: { id: Tab; labelVi: string; labelKr: string; icon: string }[] = [
    { id: "info", labelVi: "Thông tin DN", labelKr: "기업 정보", icon: "ri-building-2-line" },
    { id: "images", labelVi: "Hình ảnh", labelKr: "이미지", icon: "ri-image-2-line" },
    { id: "patents", labelVi: "Bằng sáng chế & CN", labelKr: "특허 & 인증", icon: "ri-medal-line" },
    { id: "links", labelVi: "Link website", labelKr: "웹사이트 링크", icon: "ri-links-line" },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0"
              style={logoImages[0] ? {} : { background: logoPreviewBg }}
            >
              {logoImages[0] ? (
                <img src={logoImages[0]} alt="Logo" className="w-full h-full object-contain" />
              ) : (
                <span className="text-white font-black text-sm">{form.logo_initials || "CO"}</span>
              )}
            </div>
            <div>
              <h2 className="font-bold text-gray-800 text-base">
                {editData ? t("Chỉnh sửa doanh nghiệp", "기업 수정") : t("Thêm doanh nghiệp mới", "새 기업 추가")}
              </h2>
              {form.name && <p className="text-xs text-gray-400 mt-0.5">{form.name}</p>}
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 bg-gray-50 flex-shrink-0">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap border-b-2 ${
                activeTab === tab.id
                  ? "border-[#004A99] text-[#004A99] bg-white"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <i className={tab.icon} />
              {lang === "vi" ? tab.labelVi : tab.labelKr}
              {tab.id === "patents" && patents.length > 0 && (
                <span className="bg-[#004A99] text-white text-[10px] rounded-full px-1.5 py-0.5">{patents.length}</span>
              )}
              {tab.id === "images" && companyImages.length > 0 && (
                <span className="bg-[#004A99] text-white text-[10px] rounded-full px-1.5 py-0.5">{companyImages.length}</span>
              )}
              {tab.id === "links" && extraLinks.filter((l) => l.url.trim()).length > 0 && (
                <span className="bg-[#004A99] text-white text-[10px] rounded-full px-1.5 py-0.5">{extraLinks.filter((l) => l.url.trim()).length}</span>
              )}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2">{error}</div>}

          {/* TAB 1: INFO */}
          {activeTab === "info" && (
            <>
              {/* ID field hidden from UI but still tracked in state */}
              <div>
                <label className={labelCls}>{t("Website chính", "메인 웹사이트")}</label>
                <input className={inputCls} placeholder="https://..." value={form.website ?? ""} onChange={(e) => set("website", e.target.value)} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t("Tên công ty (VI) *", "회사명 (VI) *")}</label>
                  <input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>{t("Tên công ty (KR)", "회사명 (KR)")}</label>
                  <input className={inputCls} value={form.name_kr ?? ""} onChange={(e) => set("name_kr", e.target.value)} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t("Slogan (VI)", "슬로건 (VI)")}</label>
                  <input className={inputCls} value={form.tagline_vi ?? ""} onChange={(e) => set("tagline_vi", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>{t("Slogan (KR)", "슬로건 (KR)")}</label>
                  <input className={inputCls} value={form.tagline_kr ?? ""} onChange={(e) => set("tagline_kr", e.target.value)} />
                </div>
              </div>

              <div>
                <RichTextEditor
                  value={form.description_vi ?? ""}
                  onChange={(html) => set("description_vi", html)}
                  placeholder={t("Nhập giới thiệu chi tiết về doanh nghiệp...", "기업 소개 내용 입력...")}
                  minHeight={200}
                  folder="companies"
                  label={t("Mô tả (Tiếng Việt)", "설명 (베트남어)")}
                />
              </div>
              <div>
                <RichTextEditor
                  value={form.description_kr ?? ""}
                  onChange={(html) => set("description_kr", html)}
                  placeholder={t("한국어 기업 설명 입력...", "한국어 기업 설명 입력...")}
                  minHeight={200}
                  folder="companies"
                  label={t("Mô tả (Tiếng Hàn)", "설명 (한국어)")}
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={labelCls}>{t("Năm thành lập", "설립연도")}</label>
                  <input className={inputCls} placeholder="2005" value={form.founded ?? ""} onChange={(e) => set("founded", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>{t("Địa điểm", "위치")}</label>
                  <input className={inputCls} placeholder="Seoul, Korea" value={form.location ?? ""} onChange={(e) => set("location", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>{t("Nhân viên", "직원 수")}</label>
                  <input className={inputCls} placeholder="200+" value={form.employees ?? ""} onChange={(e) => set("employees", e.target.value)} />
                </div>
              </div>

              <div>
                <label className={labelCls}>{t("Ngành chính (chọn từ danh sách)", "주요 분야 선택")}</label>
                <select
                  className={inputCls}
                  value={MAIN_CATEGORIES.find((c) => c.nameVi === form.main_category_name)?.id ?? ""}
                  onChange={(e) => {
                    const found = MAIN_CATEGORIES.find((c) => c.id === e.target.value);
                    if (found) {
                      setForm((prev) => ({ ...prev, main_category_name: found.nameVi, main_category_name_kr: found.nameKr, category_id: found.id }));
                    } else {
                      setForm((prev) => ({ ...prev, main_category_name: "", main_category_name_kr: "", category_id: "" }));
                    }
                  }}
                >
                  <option value="">{t("-- Chọn ngành chính --", "-- 분야 선택 --")}</option>
                  {MAIN_CATEGORIES.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.nameVi}</option>
                  ))}
                </select>
                {form.main_category_name && (
                  <p className="text-xs text-gray-400 mt-1">KR: {form.main_category_name_kr}</p>
                )}
              </div>

              {/* Logo Section */}
              <div className="border border-gray-200 rounded-xl p-4">
                <p className="text-xs font-bold text-gray-700 mb-3">{t("Logo Doanh Nghiệp", "회사 로고")}</p>
                <div className="flex bg-gray-100 rounded-lg p-0.5 text-xs w-fit mb-4">
                  {(["upload", "gradient"] as const).map((m) => (
                    <button
                      key={m}
                      onClick={() => setLogoMode(m)}
                      className={`px-3 py-1.5 rounded-md transition-colors cursor-pointer whitespace-nowrap font-semibold ${logoMode === m ? "bg-white text-[#004A99]" : "text-gray-500"}`}
                    >
                      {m === "upload" ? t("Upload ảnh logo", "로고 이미지 업로드") : t("Ký hiệu màu gradient", "색상 이니셜")}
                    </button>
                  ))}
                </div>

                {logoMode === "upload" ? (
                  <ImageUploader
                    images={logoImages}
                    onChange={(urls) => { setLogoImages(urls); set("logo_url", urls[0] ?? ""); }}
                    maxImages={1}
                    folder="logos"
                    label={t("Ảnh logo công ty", "회사 로고")}
                  />
                ) : (
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className={labelCls}>{t("Ký hiệu (tối đa 3 ký tự)", "이니셜 (최대 3자)")}</label>
                      <input className={inputCls} maxLength={3} value={form.logo_initials ?? ""} onChange={(e) => set("logo_initials", e.target.value.toUpperCase())} placeholder="NE" />
                    </div>
                    <div>
                      <label className={labelCls}>{t("Màu bắt đầu", "시작 색상")}</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={form.logo_from ?? "#1C4E6B"} onChange={(e) => set("logo_from", e.target.value)} className="w-9 h-9 rounded border cursor-pointer" />
                        <input className={`${inputCls} flex-1`} value={form.logo_from ?? ""} onChange={(e) => set("logo_from", e.target.value)} />
                      </div>
                    </div>
                    <div>
                      <label className={labelCls}>{t("Màu kết thúc", "끝 색상")}</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={form.logo_to ?? "#2B7A9C"} onChange={(e) => set("logo_to", e.target.value)} className="w-9 h-9 rounded border cursor-pointer" />
                        <input className={`${inputCls} flex-1`} value={form.logo_to ?? ""} onChange={(e) => set("logo_to", e.target.value)} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* TAB 2: IMAGES */}
          {activeTab === "images" && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <i className="ri-image-2-line text-[#004A99] text-lg mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-[#004A99]">
                      {t("Hình ảnh doanh nghiệp", "기업 이미지")}
                    </p>
                    <p className="text-xs text-blue-600 mt-0.5">
                      {t("Tải lên hình ảnh trực tiếp – không cần dán link. Hình ảnh sẽ hiển thị trên trang giới thiệu doanh nghiệp.", "이미지를 직접 업로드하세요. 기업 소개 페이지에 표시됩니다.")}
                    </p>
                  </div>
                </div>
              </div>
              <ImageUploader
                images={companyImages}
                onChange={setCompanyImages}
                maxImages={100}
                folder="companies"
                label={t("Hình ảnh công ty (nhà máy, sản phẩm, văn phòng...)", "회사 이미지 (공장, 제품, 사무실...)")}
              />
              {companyImages.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-2">{t("Xem trước", "미리보기")}</p>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {companyImages.map((url, i) => (
                      <div key={i} className="relative aspect-video rounded-lg overflow-hidden border border-gray-200 group">
                        <img src={url} alt={`Company ${i + 1}`} className="w-full h-full object-cover" />
                        {i === 0 && (
                          <span className="absolute top-1 left-1 text-[9px] bg-[#004A99] text-white px-1.5 py-0.5 rounded font-bold">
                            {t("Ảnh bìa", "커버")}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 3: PATENTS & CERTIFICATIONS */}
          {activeTab === "patents" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-gray-800">{t("Bằng sáng chế & Chứng nhận", "특허 & 인증서")}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{t("Tùy chọn – sẽ hiển thị trên trang giới thiệu sản phẩm nếu có.", "선택 사항 – 있는 경우 제품 소개 페이지에 표시됩니다.")}</p>
                </div>
                <button
                  onClick={addPatent}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#004A99] text-white text-xs rounded-lg cursor-pointer hover:bg-[#003d7d] transition-colors whitespace-nowrap"
                >
                  <i className="ri-add-line" />
                  {t("Thêm", "추가")}
                </button>
              </div>

              {patents.length === 0 ? (
                <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center">
                  <i className="ri-medal-line text-3xl text-gray-300 block mb-2" />
                  <p className="text-sm text-gray-400">{t("Chưa có bằng sáng chế nào", "특허 및 인증서 없음")}</p>
                  <p className="text-xs text-gray-400 mt-1">{t("Bấm \"Thêm\" để thêm bằng sáng chế hoặc chứng nhận", "\"추가\" 버튼을 눌러 등록하세요")}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {patents.map((patent, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-bold text-[#004A99]">
                          #{idx + 1} – {PATENT_TYPES.find((pt) => pt.value === patent.type)?.[lang === "vi" ? "labelVi" : "labelKr"]}
                        </span>
                        <button
                          onClick={() => removePatent(idx)}
                          className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-red-500 cursor-pointer rounded"
                        >
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <label className={labelCls}>{t("Loại", "유형")}</label>
                          <select
                            className={inputCls}
                            value={patent.type}
                            onChange={(e) => setPatent(idx, "type", e.target.value as CompanyPatent["type"])}
                          >
                            {PATENT_TYPES.map((pt) => (
                              <option key={pt.value} value={pt.value}>
                                {lang === "vi" ? pt.labelVi : pt.labelKr}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className={labelCls}>{t("Tên / Tiêu đề", "이름 / 제목")}</label>
                          <input
                            className={inputCls}
                            value={patent.title}
                            onChange={(e) => setPatent(idx, "title", e.target.value)}
                            placeholder={t("Vd: ISO 9001:2015", "예: ISO 9001:2015")}
                          />
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className={labelCls}>{t("Mô tả (tùy chọn)", "설명 (선택)")}</label>
                        <input
                          className={inputCls}
                          value={patent.description ?? ""}
                          onChange={(e) => setPatent(idx, "description", e.target.value)}
                          placeholder={t("Mô tả ngắn...", "간단한 설명...")}
                        />
                      </div>
                      <div>
                        <label className={labelCls}>{t("Hình ảnh chứng nhận (tùy chọn)", "인증서 이미지 (선택)")}</label>
                        <ImageUploader
                          images={patentImages[idx] ?? []}
                          onChange={(urls) => setPatentImg(idx, urls)}
                          maxImages={1}
                          folder="patents"
                          label={t("Ảnh chứng nhận/bằng sáng chế", "인증서/특허 이미지")}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 4: LINKS */}
          {activeTab === "links" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-gray-800">{t("Link website doanh nghiệp", "기업 웹사이트 링크")}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {t("Thêm link website gốc, trang sản phẩm, mạng xã hội... Khách hàng bấm vào sẽ ra trang gốc của doanh nghiệp.", "공식 웹사이트, 제품 페이지, SNS 등의 링크를 추가하세요.")}
                  </p>
                </div>
                <button
                  onClick={addLink}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#004A99] text-white text-xs rounded-lg cursor-pointer hover:bg-[#003d7d] transition-colors whitespace-nowrap"
                >
                  <i className="ri-add-line" />
                  {t("Thêm link", "링크 추가")}
                </button>
              </div>

              <div className="space-y-3">
                {extraLinks.map((link, idx) => (
                  <div key={idx} className="flex gap-3 items-start">
                    <div className="flex-1 grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelCls}>{t("Nhãn / Tên", "링크 이름")}</label>
                        <input
                          className={inputCls}
                          placeholder={t("Vd: Website chính, Trang sản phẩm...", "예: 메인 웹사이트, 제품 페이지...")}
                          value={link.label}
                          onChange={(e) => setLink(idx, "label", e.target.value)}
                        />
                      </div>
                      <div>
                        <label className={labelCls}>{t("URL", "URL")}</label>
                        <input
                          className={inputCls}
                          placeholder="https://..."
                          value={link.url}
                          onChange={(e) => setLink(idx, "url", e.target.value)}
                          type="url"
                        />
                      </div>
                    </div>
                    {extraLinks.length > 1 && (
                      <button
                        onClick={() => removeLink(idx)}
                        className="mt-5 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 cursor-pointer rounded-lg hover:bg-red-50 flex-shrink-0"
                      >
                        <i className="ri-delete-bin-line text-sm" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Preview active links */}
              {extraLinks.some((l) => l.url.trim()) && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs font-bold text-gray-600 mb-3">{t("Xem trước", "미리보기")}</p>
                  <div className="flex flex-wrap gap-2">
                    {extraLinks.filter((l) => l.url.trim()).map((link, idx) => (
                      <a
                        key={idx}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs text-[#004A99] hover:border-[#004A99]/50 hover:bg-[#004A99]/5 transition-colors cursor-pointer"
                      >
                        <i className="ri-external-link-line text-[10px]" />
                        {link.label || link.url}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-6 py-4 flex items-center justify-between flex-shrink-0 bg-white">
          <div className="flex gap-1">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-2 h-2 rounded-full transition-colors cursor-pointer ${activeTab === tab.id ? "bg-[#004A99]" : "bg-gray-300"}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer whitespace-nowrap transition-colors">
              {t("Hủy", "취소")}
            </button>
            <button onClick={handleSave} disabled={saving} className="px-5 py-2 bg-[#004A99] hover:bg-[#003a7a] text-white text-sm font-semibold rounded-lg cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center gap-2 transition-colors">
              {saving && <i className="ri-loader-4-line animate-spin" />}
              {t("Lưu doanh nghiệp", "저장")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyFormModal;
